/**
 * Asset System Types
 * 
 * Defines types for the asset management system including
 * models, textures, audio, and other game resources.
 */

import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";

/**
 * Asset types supported by the engine
 */
export enum AssetType {
    MODEL = "model",
    TEXTURE = "texture",
    AUDIO = "audio",
    DATA = "data",
}

/**
 * Asset loading state
 */
export enum AssetLoadState {
    PENDING = "pending",
    LOADING = "loading",
    LOADED = "loaded",
    ERROR = "error",
}

/**
 * Base asset definition
 */
export interface AssetDefinition {
    /** Unique identifier for the asset */
    id: string;
    /** Type of asset */
    type: AssetType;
    /** Path to the asset file (relative to public/assets/) */
    path: string;
    /** Optional display name */
    name?: string;
    /** Optional metadata */
    metadata?: Record<string, unknown>;
}

/**
 * Model-specific asset definition
 */
export interface ModelAssetDefinition extends AssetDefinition {
    type: AssetType.MODEL;
    /** Physics configuration for the model */
    physics?: ModelPhysicsConfig;
    /** Animation configuration */
    animations?: ModelAnimationConfig;
    /** Scale to apply on load */
    scale?: number;
}

/**
 * Physics configuration for a model
 */
export interface ModelPhysicsConfig {
    /** Whether physics is enabled */
    enabled: boolean;
    /** Collision shape type */
    shapeType: "box" | "sphere" | "capsule" | "cylinder" | "mesh" | "convex";
    /** Mass (0 = static) */
    mass?: number;
    /** Friction coefficient */
    friction?: number;
    /** Restitution (bounciness) */
    restitution?: number;
    /** Custom bounding box dimensions (overrides automatic) */
    boundingBox?: {
        width: number;
        height: number;
        depth: number;
        offset?: { x: number; y: number; z: number };
    };
    /** Collision group */
    collisionGroup?: number;
    /** Collision mask (what groups to collide with) */
    collisionMask?: number;
}

/**
 * Animation configuration for a model
 */
export interface ModelAnimationConfig {
    /** Default animation to play */
    defaultAnimation?: string;
    /** Whether to loop animations by default */
    loop?: boolean;
    /** Animation speed multiplier */
    speed?: number;
    /** Animation name mappings (friendly name -> actual animation name) */
    mappings?: Record<string, string>;
}

/**
 * Loaded model asset data
 */
export interface LoadedModelAsset {
    /** The root transform node */
    rootNode: TransformNode;
    /** All meshes in the model */
    meshes: AbstractMesh[];
    /** Animation groups */
    animationGroups: AnimationGroup[];
    /** Skeleton (if rigged) */
    skeleton: Skeleton | null;
    /** Original asset definition */
    definition: ModelAssetDefinition;
}

/**
 * Texture asset definition
 */
export interface TextureAssetDefinition extends AssetDefinition {
    type: AssetType.TEXTURE;
    /** Texture filtering mode */
    filtering?: "point" | "bilinear" | "trilinear";
    /** Whether to generate mipmaps */
    generateMipMaps?: boolean;
}

/**
 * Loaded texture asset
 */
export interface LoadedTextureAsset {
    texture: Texture;
    definition: TextureAssetDefinition;
}

/**
 * Asset loading progress callback
 */
export type AssetLoadProgressCallback = (
    loaded: number,
    total: number,
    currentAsset?: string
) => void;

/**
 * Asset catalog - defines all assets available in the game
 */
export interface AssetCatalog {
    models: Record<string, ModelAssetDefinition>;
    textures: Record<string, TextureAssetDefinition>;
}

/**
 * Pre-defined collision groups
 * Re-exported from core/CollisionGroups for unified collision layer system
 * @deprecated Use CollisionGroup enum from core/CollisionGroups instead
 */
import { CollisionGroup } from "../core/CollisionGroups";

export const CollisionGroups = {
    DEFAULT: CollisionGroup.DEFAULT,
    TERRAIN: CollisionGroup.TERRAIN,
    ENTITY: CollisionGroup.DYNAMIC,        // Maps to DYNAMIC
    PLAYER: CollisionGroup.PLAYER,
    NPC: CollisionGroup.NPC,
    PROJECTILE: CollisionGroup.PROJECTILE,
    TRIGGER: CollisionGroup.TRIGGER,
    INTERACTABLE: CollisionGroup.INTERACTABLE,
    VEGETATION: CollisionGroup.STATIC,     // Vegetation uses STATIC
    STRUCTURE: CollisionGroup.STATIC,      // Structures use STATIC
    ALL: CollisionGroup.ALL,
} as const;
